import { ChangeDetectorRef, Component, OnDestroy, ViewContainerRef } from '@angular/core';
import { AngularComponentFactory } from 'survey-angular-ui';
import { LicenseValidationFormWidgetModel } from './license-validation-form.widget.model';
import { RendererFactory } from 'survey-core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  SnackbarService,
  StatesApiService
} from '@tangilla/tg-ng-core';
import { LicenseValidationFormWidgetJson } from './license-validation-form.widget.json';
import { License } from '@tangilla/tg-ng-models';
import { SurveyService } from '../../../../core/services/survey/survey.service';
import { QuestionsService } from '../../../../core/services/survey/questions.service';
import { VariablesService } from '../../../../core/services/survey/variables.service';
import { LicenseValidationValidationsService } from './service/license-validation-validations.service';
import { TangillaQuestion } from '../tangilla-question';
import { LicenseValidationFormWidgetService } from './service/license-validation-form.widget.service';
import { Subscription } from 'rxjs';
import { ErrorsService } from '../../../../core/services/utils/errors.service';

@Component({
  selector: 'tgf-license-validation-form-widget',
  templateUrl: './license-validation-form.widget.component.html',
  styleUrls: ['./license-validation-form.widget.component.scss']
})
export class LicenseValidationFormWidgetComponent extends TangillaQuestion<LicenseValidationFormWidgetModel> implements OnDestroy {
  isLoading = false;
  isLoadingLicense = false;

  corpOnly = false;
  licenseValidationFormWidgetJson = new LicenseValidationFormWidgetJson();
  validLicense?: License;
  questionJson: any;

  licenseValidatedName?: string;
  licenseSearchSubscription?: Subscription;
  stateLabel = '';
  constructor(
    private statesApiService: StatesApiService,
    public variablesService: VariablesService,
    private licenseValidationFormWidgetService: LicenseValidationFormWidgetService,
    private licenseValidationValidationsService: LicenseValidationValidationsService,
    private errorsService: ErrorsService,
    formBuilder: FormBuilder,
    surveyService: SurveyService,
    questionsService: QuestionsService,
    changeDetectorRef: ChangeDetectorRef,
    viewContainerRef?: ViewContainerRef
  ) {
    super(formBuilder, surveyService, questionsService, changeDetectorRef, viewContainerRef);
    this.formBuilder = formBuilder;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.stateLabel = this.surveyService.surveyModel.getVariable('v-state_long');
    this.licenseValidatedName = `license_validated_${this.questionName}`;
    this.setJsonObjectValues();
    this.initForm();
    this.licenseValidationFormWidgetJson.showValidLicenseFields = this.variablesService.getVariable(this.licenseValidatedName);
    if (!this.variablesService.getVariable(this.licenseValidatedName)) {
      this.setLicenseSearchObservable();
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.licenseSearchSubscription?.unsubscribe();
  }

  setJsonObjectValues() {
    if (this.model['jsonObj']) {
      this.corpOnly = this.model['jsonObj'].corpOnly;
    }
  }

  initForm() {
    this.addLicenseValidationFormControls();
    this.mapQuestionJson();
  }

  addLicenseValidationFormControls() {
    this.addFormControl('license_number', this.getQuestionName('licenseNumber', 'LicNumber'), [Validators.required], this.getQuestionValue('licenseNumber', 'LicNumber'), this.variablesService.getVariable(this.licenseValidatedName!));
    this.addFormControl('license_name', this.getQuestionName('licenseName', 'LicName'), [Validators.required], this.getQuestionValue('licenseName', 'LicName'), true);
    this.addFormControl('license_state', this.getQuestionName('licenseState', 'LicState'), [Validators.required], this.getQuestionValue('licenseState', 'LicState'), true);
    this.addFormControl('license_type', this.getQuestionName('licenseType', 'LicType'), [Validators.required], this.getQuestionValue('licenseType', 'LicType'), true);
    this.addFormControl('license_status', this.getQuestionName('licenseStatus', 'LicStatus'), [Validators.required], this.getQuestionValue('licenseStatus', 'LicStatus'), true);
    this.addFormControl('license_expiry', this.getQuestionName('licenseExpiry', 'LicExp'), [Validators.required], this.getQuestionValue('licenseExpiry', 'LicExp'), true);

    this.addFormControl('broker_license_number', 'BrkLic', [], this.questionsService.brokerLicenseNumber?.value || null, true);
    this.addFormControl('broker_license_name', 'BrkName', [], this.questionsService.brokerLicenseName?.value || null, true);
    this.addFormControl('corporate_license_number', 'CorpLic', [], this.questionsService.corporateLicenseNumber?.value || null, true);
    this.addFormControl('broker_license_type', 'BrkLicType', [], this.questionsService.brokerLicenseType?.value || null, true);
  }

  mapQuestionJson() {
    const questionJson = this.licenseValidationFormWidgetJson.getWidgetJson(this.questionForm, this.questionModel);
    questionJson.valueChangesFunctions = {};
    questionJson.valueChangesFunctions.license_number =
      (licenseNumber: string) => this.licenseNumberValueChanges();
    this.questionJson = questionJson;
  }

  licenseNumberValueChanges() {
    this.licenseValidationFormWidgetJson.showValidLicenseFields = false;
    this.setFormControlValue('license_name', null);
    this.setFormControlValue('license_state', null);
    this.setFormControlValue('license_type', null);
    this.setFormControlValue('license_status', null);
    this.setFormControlValue('license_expiry', null);

    this.setFormControlValue('broker_license_number', null);
    this.setFormControlValue('broker_license_name', null);
    this.setFormControlValue('corporate_license_number', null);
    this.setFormControlValue('broker_license_type', null);
    this.changeDetectorRef.detectChanges();
  }

  formToEntity() {
    return this.questionForm.getRawValue();
  }

  setLicenseSearchObservable() {
    if (!this.licenseValidationFormWidgetService
      .licenseSubjects[this.getQuestionName('licenseNumber', 'LicNumber')]) {
      this.licenseValidationFormWidgetService.setLicenseQuestionSubject(this.getQuestionName('licenseNumber', 'LicNumber'));
    }

    this.licenseSearchSubscription = this.licenseValidationFormWidgetService
      .licenseSubjects[this.getQuestionName('licenseNumber', 'LicNumber')]?.licenseSearch$
      .subscribe(licenseNumber => {
        if (licenseNumber) {
          this.validateLicense(licenseNumber);
        }
      });
  }

  validateLicense(licenseNumber?: string) {
    const that = this;
    if (licenseNumber) {
      this.setFormControlValue('license_number', licenseNumber);
    }
    this.submitForm(() => that.doValidateLicense());
  }

  doValidateLicense() {
    this.isLoadingLicense = true;
    this.model.clearErrors();
    this.model.value = undefined;

    if (!this.isValidSurveyLastName()) {
      this.model.addError('Last Name and License Number are required to search and validate your License.');
      this.isLoadingLicense = false;
      return;
    }

    this.changeDetectorRef.detectChanges();
    const body = this.formToEntity();

    const params = this.getStateLicenseParams();

    const stateAbbreviation = this.surveyService.surveyModel.getVariable('v-state');
    this.statesApiService.getStateLicense(stateAbbreviation, body.license_number, params)
      .subscribe({
        next: (data: License) => {
          if (this.licenseValidationValidationsService.validateLicense(this.questionModel, data)) {
            data.state_abbreviation = stateAbbreviation;
            this.validLicense = data;
            if (this.validLicense) {
              this.questionForm.get('license_number')?.disable();
              this.variablesService.setVariable(this.licenseValidatedName!, true);
            }
            this.mapValidLicenseFields();
            this.licenseValidationFormWidgetJson.showValidLicenseFields = true;
            this.model.value = this.validLicense;
          }
          this.isLoadingLicense = false;
          this.changeDetectorRef.detectChanges();
          return;
        },
        error: error => {
          this.variablesService.setVariable(this.licenseValidatedName!, false);
          if(error.error && error.error.error && error.error.error.status === 409) {
            this.model.addError(error.error.error.message);
          } else {
            this.model.addError('This license is not valid');
          }
          this.isLoadingLicense = false;
          this.errorsService.processError(error, 'soft');
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  validateAgain() {
    this.model.clearErrors();
    this.model.value = undefined;

    this.validLicense = undefined;
    this.variablesService.setVariable(`license_validated_${this.model.name}`, false);
    this.questionForm.get('license_number')?.enable();
    this.questionForm.get('license_number')?.setValue(null);
    this.questionForm.get('license_number')?.updateValueAndValidity();
    this.licenseNumberValueChanges();
  }

  isValidSurveyLastName() {
    const lastNameQuestion = this.surveyService.surveyModel.getQuestionByName('LastName');

    return lastNameQuestion && !!lastNameQuestion.value;
  }

  mapValidLicenseFields() {
    this.setFormControlValue('license_name', this.validLicense?.license_name);
    this.setFormControlValue('license_state', this.validLicense?.state_abbreviation);
    this.setFormControlValue('license_type', this.validLicense?.license_type);
    this.setFormControlValue('license_status', this.validLicense?.license_status);
    this.setFormControlValue('license_expiry', this.validLicense?.license_expiry);

    this.setFormControlValue('broker_license_number', this.validLicense?.broker_license_number);
    this.setFormControlValue('broker_license_name', this.validLicense?.broker_name);
    this.setFormControlValue('corporate_license_number', this.validLicense?.corporate_license_number);
    this.setFormControlValue('broker_license_type', this.validLicense?.broker_license_type);
  }

  getStateLicenseParams() {
    let params: any = {};
    if (!this.corpOnly && this.surveyService.isThompsonState) { // TODO: re-review this
      params = {
        params: {
          ignore_affiliation: true,
          active_only: true,
          include_broker_relationship: true
        }
      };
    } else {
      params = {
        params: {
          ignore_affiliation: true,
          active_only: true
        }
      };
    }

    if (this.surveyService.surveyModel.getVariable('v-state') === 'WA' && this.surveyService.surveyModel.getQuestionByName('LastName')?.value) {
      params.params.last_name = this.surveyService.surveyModel.getQuestionByName('LastName')?.value;
    }
    return params;
  }

  isItLoading() {
    return this.isLoading || this.isLoadingLicense;
  }
}

RendererFactory.Instance.registerRenderer(
  'license_validation_form',
  'license_validation_form',
  'tg_license_validation',
  true
);
AngularComponentFactory.Instance.registerComponent('tg_license_validation', LicenseValidationFormWidgetComponent);
